import { useCallback, useEffect, useRef, useState } from "react";
import { Bell, X, Check } from "@phosphor-icons/react";
import api from "@/lib/api";

export default function NotificationCenter() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const refresh = useCallback(() => {
    api.get("/notifications")
      .then((r) => { setItems(r.data.items || []); setUnread(r.data.unread || 0); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 30000);
    return () => clearInterval(t);
  }, [refresh]);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const dismiss = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      refresh();
    } catch { /* noop */ }
  };

  const dismissAll = async () => {
    try {
      await api.post("/notifications/read-all");
      refresh();
    } catch { /* noop */ }
  };

  return (
    <div className="relative" ref={ref}>
      <button type="button" data-testid="notification-bell" onClick={() => setOpen(!open)}
        className="relative rounded-sm border border-input p-2 text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground">
        <Bell size={18} />
        {unread > 0 && (
          <span data-testid="notification-unread-badge"
            className="absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-black text-primary-foreground">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div data-testid="notification-panel"
          className="absolute right-0 top-12 z-50 w-80 rounded-sm border border-border bg-background shadow-2xl sm:w-96">
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Notifications</span>
            {unread > 0 && (
              <button type="button" data-testid="notification-read-all" onClick={dismissAll}
                className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-primary hover:text-amber-400">
                <Check size={12} /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <p className="px-4 py-10 text-center text-sm text-muted-foreground" data-testid="notification-empty">
                No notifications yet.
              </p>
            ) : items.map((n) => (
              <div key={n.id} data-testid={`notification-item-${n.id}`}
                className={`flex items-start gap-3 border-b border-border/50 px-4 py-3 ${n.read ? "opacity-50" : ""}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold">{n.title}</p>
                  <p className="mt-0.5 break-words text-xs text-muted-foreground">{n.body}</p>
                  <p className="mt-1 font-mono text-[10px] text-muted-foreground">
                    {(n.created_at || "").slice(0, 16).replace("T", " ")}
                  </p>
                </div>
                {!n.read && (
                  <button type="button" data-testid={`notification-dismiss-${n.id}`} onClick={() => dismiss(n.id)}
                    className="shrink-0 rounded-sm p-1 text-muted-foreground transition-colors hover:bg-destructive/20 hover:text-red-400">
                    <X size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
